export enum UserRole {
  Operador = 'operador',
  Gestion = 'gestion',
}

export enum UserStatus {
  Activo = 'ACTIVO',
  Inactivo = 'INACTIVO',
  Pausado = 'PAUSADO',
}

export interface ApiUser {
  id_user: number;
  user_name: string;
  first_name: string;
  last_name: string | null;
  email: string;
  phone_number: string | null;
  role_name: string;
  status: string;
  /**
   * Empresas asociadas al usuario. El endpoint actual devuelve `string[]`
   * (ej. `["Cruzeiro F.C"]`); endpoints heredados podían devolverla como
   * string único bajo `company_name`/`unit_name`/`business_unit_name`.
   */
  company_name?: string | string[] | null;
  unit_name?: string | null;
  business_unit_name?: string | null;
  /**
   * Comercios asociados. El backend actual devuelve `merchants: { id_merchant, name }[]`.
   * Se mantienen las formas legadas (`merchant_name` string, `merchant_names` string[],
   * `merchants` con shapes alternos) para tolerar respuestas anteriores.
   */
  merchant_name?: string;
  merchant_names?: string[];
  merchants?: Array<string | { id_merchant?: number; name?: string; merchant_name?: string; legal_name?: string }>;
}

export interface ApiUsersResponse {
  success: boolean;
  message: string;
  total: number;
  page: number;
  limit: number;
  data: ApiUser[];
}

export interface UserModel {
  id: number;
  nombre: string;
  /** Nombre tal cual viene del API (`first_name`). */
  firstName?: string;
  /** Apellido tal cual viene del API (`last_name`). */
  lastName?: string | null;
  /** Username tal cual viene del API (`user_name`). */
  userName?: string;
  email: string;
  rol: string;
  permiso: UserRole;
  estado: UserStatus;
  password?: string;
  /** Teléfono tal como viene del API (lista usuarios). */
  phoneNumber?: string | null;
  /** Empresa / unidad de negocio a la que pertenece el usuario. */
  empresa?: string;
  descripcion?: string;
  cliente?: string;
  comercios?: string[];
  /**
   * IDs de comercios asignados al usuario, alineados con `comercios` por orden
   * cuando vienen del backend. Se usan para armar el payload de
   * `/user/create-access` sin tener que resolver por nombre.
   */
  merchantIds?: number[];
  detalle?: Record<string, unknown>;
}

export interface CreateUserPayload {
  first_name: string;
  last_name: string;
  user_name: string;
  phone_number?: string;
  email: string;
  password: string;
}

/** Respuesta de POST /auth/create — el backend devuelve el `id_user` recién creado. */
export interface CreateUserResponse {
  id_user: number;
  [key: string]: unknown;
}

/**
 * Cuerpo PATCH /auth/update/:id_user — el backend admite actualización parcial.
 * Propiedades aceptadas por el API: email, first_name, last_name, password,
 * phone_number, user_name. Las relaciones empresa/unidad/comercios se asignan
 * por separado vía POST /user/create-access (ver `AssignAccessPayload`).
 */
export interface UpdateUserPayload {
  first_name?: string;
  last_name?: string;
  user_name?: string;
  email?: string;
  password?: string;
  phone_number?: string;
}

/**
 * Cuerpo POST /user/create-access — asigna empresas/unidades/comercios a un
 * usuario. La estructura es jerárquica: una compañía agrupa unidades, y cada
 * unidad agrupa cuentas (comercios). Se usa tanto en alta como en edición de
 * accesos: al re-enviar para un `id_user` existente, los accesos se reemplazan.
 */
export interface AssignAccessAccount {
  id_merchant: number;
}

export interface AssignAccessUnit {
  id_unit: number;
  accounts: AssignAccessAccount[];
}

export interface AssignAccessCompany {
  id_company: number;
  units: AssignAccessUnit[];
}

export interface AssignAccessPayload {
  id_user: number;
  email: string;
  father_account: boolean;
  companies: AssignAccessCompany[];
}


const ROLE_MAP: Record<string, UserRole> = {
  OPERADOR: UserRole.Operador,
  GESTION: UserRole.Gestion,
};

const STATUS_MAP: Record<string, UserStatus> = {
  ACTIVO: UserStatus.Activo,
  INACTIVO: UserStatus.Inactivo,
  PAUSADO: UserStatus.Pausado,
};

function normalizeRole(roleName: string): UserRole {
  return ROLE_MAP[roleName.toUpperCase()] ?? UserRole.Operador;
}

function normalizeStatus(status: string): UserStatus {
  return STATUS_MAP[status.toUpperCase()] ?? UserStatus.Inactivo;
}

function buildFullName(firstName: string, lastName: string | null): string {
  return lastName ? `${firstName} ${lastName}` : firstName;
}


function extractEmpresa(api: ApiUser): string | undefined {
  const raw = api.company_name ?? api.unit_name ?? api.business_unit_name;
  // Hoy el endpoint devuelve `company_name: string[]`. Tomamos el primer
  // elemento no vacío. Mantenemos el path de string único para tolerar
  // respuestas heredadas.
  if (Array.isArray(raw)) {
    const first = raw.find((v) => typeof v === 'string' && v.trim().length > 0);
    return typeof first === 'string' ? first.trim() : undefined;
  }
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function extractComercios(api: ApiUser): string[] {
  if (Array.isArray(api.merchant_names)) {
    return api.merchant_names.filter((v): v is string => typeof v === 'string' && v.length > 0);
  }
  if (Array.isArray(api.merchants)) {
    return api.merchants
      .map(m => {
        if (typeof m === 'string') return m;
        return m?.legal_name ?? m?.name ?? m?.merchant_name ?? '';
      })
      .filter(v => typeof v === 'string' && v.length > 0);
  }
  return api.merchant_name ? [api.merchant_name] : [];
}

/**
 * Extrae los `id_merchant` del array `merchants` cuando el backend los provee
 * como objetos `{ id_merchant, name }`. Se preserva el orden para que coincida
 * posición a posición con el array de nombres de `extractComercios`.
 */
function extractMerchantIds(api: ApiUser): number[] {
  if (!Array.isArray(api.merchants)) return [];
  return api.merchants
    .map((m) => (typeof m === 'object' && m && Number.isFinite(Number(m.id_merchant)) ? Number(m.id_merchant) : NaN))
    .filter((n): n is number => Number.isFinite(n));
}

export function mapApiUserToModel(api: ApiUser): UserModel {
  return {
    id: api.id_user,
    nombre: buildFullName(api.first_name, api.last_name),
    firstName: api.first_name,
    lastName: api.last_name,
    userName: api.user_name,
    email: api.email,
    rol: api.role_name,
    permiso: normalizeRole(api.role_name),
    estado: normalizeStatus(api.status),
    phoneNumber: api.phone_number ?? null,
    empresa: extractEmpresa(api),
    comercios: extractComercios(api),
    merchantIds: extractMerchantIds(api),
  };
}

export function mapApiUsersToModels(apiUsers: ApiUser[]): UserModel[] {
  return apiUsers.map(mapApiUserToModel);
}

